import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Switch,
  FormControlLabel,
  TextField,
  Typography,
  Collapse,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  MenuItem,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { createAudioTranscription, type AudioLanguage } from "../../../actions/audio-transcription/createAudioTranscription";
import { useAudioLanguagePreference } from "../../../hooks/useAudioLanguagePreference";
import Dropzone, { AUDIO_ACCEPT } from "../../../components/forms/GenericDropzone";
import UploadedFilesList from "../../../components/lists/UploadedFilesList";
import PdfPasswordModal from "../../../components/modals/PdfPasswordModal";
import InlineProcessingStatus from "../../../components/processing/InlineProcessingStatus";
import TransformPanel from "../../../components/transform/TransformPanel";
import { useUploadForm } from "../../../hooks/useUploadForm";
import { useTransform } from "../../../hooks/useTransform";
import AudioTranscriptionList from "../../audio-transcription/AudioTranscriptionList";
import {
  LEGAL_VOCAB_PRESET,
  SHOW_VOCABULARY_PANEL,
  scribePrimaryCtaContainerSx,
  scribeResetButtonSx,
} from "../../../constants/scribeUi";
import GlassPanel from "../GlassPanel";

const parseSpeakerNames = (input: string): Record<string, string> | undefined => {
  const entries: Array<[string, string]> = [];
  for (const rawLine of input.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line) continue;
    const match = line.match(/^speaker\s*(\d+)\s*[:=-]\s*(.+)$/i);
    if (!match) continue;
    entries.push([`Speaker ${match[1]}`, match[2].trim()]);
  }
  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
};

const AudioTab: React.FC<{ isApproved: boolean }> = ({ isApproved }) => {
  const uploadForm = useUploadForm({ minFiles: 1, maxFiles: 1, maxTotalSizeMB: 200, maxSingleFileSizeMB: 200 });
  const transform = useTransform();
  const [downloadTarget, setDownloadTarget] = useState("");
  const [originalDownloadTarget, setOriginalDownloadTarget] = useState("");

  useEffect(() => {
    if (transform.transformDownloadKey) {
      setDownloadTarget(transform.transformDownloadKey);
    } else if (originalDownloadTarget) {
      setDownloadTarget(originalDownloadTarget);
    }
  }, [transform.transformDownloadKey, originalDownloadTarget]);

  const [dictationMode, setDictationMode] = useState(true);
  const [aiCorrection, setAiCorrection] = useState(true);
  const [speakerIdentification, setSpeakerIdentification] = useState(false);
  const [numSpeakers, setNumSpeakers] = useState("auto");
  const [speakerNamesText, setSpeakerNamesText] = useState("");
  const [vocabulary, setVocabulary] = useState("");
  const { language, setLanguage } = useAudioLanguagePreference();
  const speakerNames = useMemo(() => parseSpeakerNames(speakerNamesText), [speakerNamesText]);

  const uploadHandler = uploadForm.createApiUploadHandler(
    async (fileKeys, filePasswords) =>
      createAudioTranscription(fileKeys, {
        dictationMode,
        aiCorrection,
        speakerIdentification,
        numSpeakers: speakerIdentification && numSpeakers !== "auto" ? Number(numSpeakers) || undefined : undefined,
        speakerNames,
        vocabulary: SHOW_VOCABULARY_PANEL ? vocabulary : LEGAL_VOCAB_PRESET,
        filePasswords,
        enableDocumentChat: uploadForm.enableDocumentChat,
        language,
      }),
    () => {
      setSpeakerNamesText("");
      setVocabulary("");
      setSpeakerIdentification(false);
      setNumSpeakers("auto");
    },
  );

  const handleSubmit = (e: React.FormEvent) => uploadForm.handleSubmit(e);

  // #402 — start a fresh transcription without refreshing the page. Clears the
  // finished transcript + the uploaded file + job state (handleModalClose also
  // refreshes the Recent Transcriptions list so the just-completed run appears),
  // returning the tab to a clean upload state.
  //
  // #611 — this used `transform.reset()`, which is the transform-UNDO primitive:
  // it restores displayText from originalText and never clears the source, so
  // `hasText` stayed true and the previous transcript (plus its whole transform
  // toolbar) survived the reset. `setSourceText("")` clears both, which is what
  // "start fresh" actually means.
  const handleNewUpload = useCallback(() => {
    transform.setSourceText("");
    uploadForm.handleModalClose();
    setDownloadTarget("");
    setOriginalDownloadTarget("");
  }, [transform, uploadForm]);

  // A result is on screen once the transcript has streamed in (or a job completed).
  const hasResult = transform.hasText || (!uploadForm.showLoader && !!uploadForm.jobId);

  return (
    <>
      <Typography
        sx={{
          fontFamily: "'Syne', sans-serif",
          fontSize: "0.8rem",
          fontWeight: 700,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          color: "var(--teal)",
          mb: 1,
        }}
      >
        Audio to Text
      </Typography>

      <GlassPanel>
        {hasResult && (
          <Box sx={{ mb: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              size="large"
              onClick={handleNewUpload}
              disabled={uploadForm.processing}
              sx={scribeResetButtonSx}
            >
              ＋ New audio file
            </Button>
          </Box>
        )}
        <Box sx={{ opacity: uploadForm.processing ? 0.65 : 1, pointerEvents: uploadForm.processing ? "none" : "auto" }}>
        <form onSubmit={handleSubmit}>
          <Dropzone
            onDrop={uploadForm.createDropHandler(isApproved)}
            multiple={false}
            mainText="Upload Audio File"
            subText="Drag & drop or click to select an audio file"
            buttonText="Select Audio (.mp3, .mp4, .m4a, .wav, .flac, .webm, .ogg, .opus)"
            acceptOverride={AUDIO_ACCEPT}
          />
          <UploadedFilesList
            title="Selected File"
            files={uploadForm.files}
            onRemoveFile={uploadForm.removeFile}
            filePasswords={uploadForm.filePasswords}
          />
          {uploadForm.fileError && (
            <Typography color="error" sx={{ mt: 1, fontSize: "0.82rem" }}>
              {uploadForm.fileError}
            </Typography>
          )}

          <Box sx={{ display: "flex", gap: 2, mt: 2, alignItems: "center" }}>
            <TextField
              select
              label="Language"
              value={language}
              onChange={(e) => setLanguage(e.target.value as AudioLanguage)}
              size="small"
              sx={{ width: 180 }}
              inputProps={{ "aria-label": "Audio language" }}
            >
              <MenuItem value="af">Afrikaans</MenuItem>
              <MenuItem value="en">English</MenuItem>
            </TextField>
          </Box>

          <Box sx={{ display: "flex", gap: 3, mt: 2, flexWrap: "wrap" }}>
            <FormControlLabel
              control={<Switch checked={dictationMode} onChange={(e) => setDictationMode(e.target.checked)} />}
              label="Dictation Mode"
              sx={{ "& .MuiTypography-root": { fontSize: "0.82rem", fontWeight: 500 } }}
            />
            <FormControlLabel
              control={<Switch checked={aiCorrection} onChange={(e) => setAiCorrection(e.target.checked)} />}
              label="AI Correction"
              sx={{ "& .MuiTypography-root": { fontSize: "0.82rem", fontWeight: 500 } }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={speakerIdentification}
                  onChange={(e) => setSpeakerIdentification(e.target.checked)}
                />
              }
              label="Identify Speakers"
              sx={{ "& .MuiTypography-root": { fontSize: "0.82rem", fontWeight: 500 } }}
            />
          </Box>

          <Collapse in={speakerIdentification}>
            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
              <TextField
                select
                label="Number of speakers"
                value={numSpeakers}
                onChange={(e) => setNumSpeakers(e.target.value)}
                size="small"
                sx={{ width: 200 }}
              >
                <MenuItem value="auto">Auto-detect</MenuItem>
                {Array.from({ length: 9 }, (_, i) => i + 2).map((n) => (
                  <MenuItem key={n} value={String(n)}>{n} speakers</MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Speaker names (optional)"
                value={speakerNamesText}
                onChange={(e) => setSpeakerNamesText(e.target.value)}
                placeholder={"Speaker 1: Attorney\nSpeaker 2: Client"}
                helperText="One per line: Speaker 1: Name"
                size="small"
              />
            </Box>
          </Collapse>

          {SHOW_VOCABULARY_PANEL && (
            <Accordion
              sx={{
                mt: 2,
                backgroundColor: "var(--s2)",
                border: "1px solid var(--border)",
                "&::before": { display: "none" },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "var(--t2)" }} />}>
                <Typography sx={{ fontSize: "0.82rem", fontWeight: 500, color: "var(--t2)" }}>
                  📘 Vocabulary &amp; Term Guide
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ backgroundColor: "var(--s1)", borderTop: "1px solid var(--border)" }}>
                <Typography sx={{ fontSize: "0.78rem", color: "var(--t2)", mb: 1 }}>
                  Add names, terms, or jargon that AI-SCRIBE should spell correctly. Comma-separated.
                </Typography>
                <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    value={vocabulary}
                    onChange={(e) => setVocabulary(e.target.value)}
                    placeholder="Enter names, acronyms, technical terms…"
                    size="small"
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      const current = vocabulary.trim();
                      setVocabulary(current ? current + ", " + LEGAL_VOCAB_PRESET : LEGAL_VOCAB_PRESET);
                    }}
                    sx={{ whiteSpace: "nowrap", minWidth: "auto" }}
                  >
                    ⚖️ Legal
                  </Button>
                </Box>
              </AccordionDetails>
            </Accordion>
          )}

          <Box sx={[{ mt: 3 }, scribePrimaryCtaContainerSx]}>
            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={!isApproved || uploadForm.files.length !== 1 || uploadForm.processing}
            >
              ⚡ Transcribe Audio
            </Button>
          </Box>
        </form>
        </Box>
        <InlineProcessingStatus
          compact
          open={uploadForm.showLoader}
          files={uploadForm.files}
          jobId={uploadForm.jobId}
          userId={uploadForm.userId}
          onUpload={uploadHandler}
          onComplete={async () => {
            if (uploadForm.jobId) await uploadForm.checkDocumentChatStatus(uploadForm.jobId);
            uploadForm.setProcessing(false);
          }}
          onTextExtracted={transform.streamSourceText}
          onDownloadReady={(key: string) => { setDownloadTarget(key); setOriginalDownloadTarget(key); }}
          onClose={uploadForm.handleModalClose}
          chatEnabled={uploadForm.documentChatStatus?.chatEnabled}
          hasDocumentChatAccess={uploadForm.hasDocumentChatAccess}
          documentTitle="Audio Transcription"
        />
        <TransformPanel
          displayText={transform.displayText}
          hasText={transform.hasText}
          isTransforming={transform.isTransforming}
          isStreamingResult={transform.isStreamingResult}
          isSourceStreaming={transform.isSourceStreaming}
          transformError={transform.transformError}
          downloadUrl={downloadTarget}
          inlineChatJobId={uploadForm.jobId}
          inlineChatFileName="Audio Transcription"
          onTransform={transform.runTransform}
          onReset={transform.reset}
        />
      </GlassPanel>
      <PdfPasswordModal
        open={uploadForm.showPasswordModal}
        file={uploadForm.currentFileNeedingPassword}
        onSubmit={uploadForm.handlePasswordSubmit}
        onSkip={uploadForm.handlePasswordSkip}
        onCancel={uploadForm.handlePasswordCancel}
      />

      <Box sx={{ mt: 3 }}>
        <Typography
          sx={{
            fontFamily: "'Syne', sans-serif",
            fontSize: "0.8rem",
            fontWeight: 700,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: "var(--teal)",
            mb: 1,
          }}
        >
          Recent Transcriptions
        </Typography>
        <GlassPanel>
          <AudioTranscriptionList
            refresh={uploadForm.refresh}
            onRefreshComplete={uploadForm.handleRefreshComplete}
          />
        </GlassPanel>
      </Box>
    </>
  );
};

export default AudioTab;
